/*
 * Project: Goalscape
 * File:    firebase.js
 * Path:    src/firebase.js
 * Description: Initializes Firebase and exports Firebase services.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement server-side validation for Firebase configuration to prevent deployment issues.
 */
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, doc, onSnapshot, updateDoc, addDoc, getDocs, getDoc, deleteDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

console.log('Firebase Config:', {
    ...firebaseConfig,
    apiKey: firebaseConfig.apiKey ? 'exists' : 'missing',
});

const app = initializeApp(firebaseConfig);

/*
 * Description: Exports the Firestore database instance.
 * AI Hint: Consider using Firebase emulators for local development and testing.
 */
export const db = getFirestore(app);

/*
 * Description: Exports the Firebase authentication instance.
 * AI Hint: Implement multi-factor authentication for enhanced security.
 */
export const auth = getAuth(app);

console.log('Firebase initialized:', app.name);

/*
 * Function: createProject
 * Description: Adds a new project to the 'projects' collection in Firestore.
 * Parameters:
 *   projectData: object - The data for the new project.
 * Returns: Promise<DocumentReference> - A promise that resolves with the DocumentReference of the newly created project.
 * AI Hint: Implement server-side functions or triggers to automatically initialize project settings.
 */
export const createProject = async (projectData) => {
    return await addDoc(collection(db, 'projects'), projectData);
};

/*
 * Function: updateProject
 * Description: Updates an existing project in Firestore.
 * Parameters:
 *   projectId: string - The ID of the project to update.
 *   updates: object - The updates to apply to the project.
 * Returns: Promise<void> - A promise that resolves when the update is complete.
 * AI Hint: Use transactions to ensure atomicity when updating multiple project properties.
 */
export const updateProject = async (projectId, updates) => {
    const projectRef = doc(db, 'projects', projectId);
    return await updateDoc(projectRef, updates);
};

/*
 * Function: getProjects
 * Description: Retrieves all projects from the 'projects' collection in Firestore.
 * Parameters: None
 * Returns: Promise<Array<object>> - A promise that resolves with an array of project objects.
 * AI Hint: Implement pagination for large project collections to improve performance.
 */
export const getProjects = async () => {
    const querySnapshot = await getDocs(collection(db, 'projects'));
    return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));
};

/*
 * Description: Exports the Firebase storage instance.
 * AI Hint: Implement server-side functions or triggers to automatically resize and optimize uploaded images.
 */
export const storage = getStorage(app);

/**
 * Adds a new cover image document to the 'coverImages' collection.
 * @param {string} base64String - The base64 encoded image string.
 * @returns {Promise<string>} - A promise that resolves with the new document's ID.
 */
export const addCoverImage = async (base64String) => {
    try {
        const docRef = await addDoc(collection(db, 'coverImages'), {
            base64Image: base64String,
            createdAt: new Date().toISOString(),
        });
        return docRef.id;
    } catch (error) {
        console.error("Error adding cover image: ", error);
        throw error;
    }
};

/**
 * Fetches a cover image document from the 'coverImages' collection.
 * @param {string} imageId - The ID of the cover image document.
 * @returns {Promise<object|null>} - A promise that resolves with the image data or null if not found.
 */
export const getCoverImage = async (imageId) => {
    try {
        const docRef = doc(db, 'coverImages', imageId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.log("No such cover image document!");
            return null;
        }
    } catch (error) {
        console.error("Error getting cover image: ", error);
        throw error;
    }
};

/**
 * Deletes a cover image document from the 'coverImages' collection.
 * @param {string} imageId - The ID of the cover image document to delete.
 * @returns {Promise<void>} - A promise that resolves when the deletion is complete.
 */
export const deleteCoverImage = async (imageId) => {
    try {
        const docRef = doc(db, 'coverImages', imageId);
        await deleteDoc(docRef);
    } catch (error) {
        console.error("Error deleting cover image: ", error);
        throw error;
    }
};
